import type { CustomFieldConfig } from "./spec.ts";

/** Decimals a `number` field keeps when its config names none. Two is the
 *  everyday precision (money's, a percentage's) and leaves a typed
 *  `.63434` room to round rather than refuse. */
export const DEFAULT_NUMBER_DECIMAL_PLACES = 2;

/** The most decimals a `number` field can be configured to keep. Past six the
 *  display stops reading as a quantity, and a double loses cents-level
 *  exactness once the integer part grows. */
export const MAX_NUMBER_DECIMAL_PLACES = 6;

/** The decimals a number field keeps: its config, clamped to the range the
 *  settings form offers, or the default when the config names none. */
export function numberDecimalPlaces(
  config: CustomFieldConfig | null | undefined,
): number {
  const places = config?.decimalPlaces;
  if (typeof places !== "number" || !Number.isFinite(places)) {
    return DEFAULT_NUMBER_DECIMAL_PLACES;
  }
  return Math.min(MAX_NUMBER_DECIMAL_PLACES, Math.max(0, Math.round(places)));
}

/** Whether a number field groups its integer digits: its config, or off when
 *  the config does not say. Bare digits are the safe default — an identifier
 *  or a year reads as `20231`, never `20,231` — and a quantity opts in. */
export function numberThousandsSeparator(
  config: CustomFieldConfig | null | undefined,
): boolean {
  return config?.thousandsSeparator === true;
}

/** The rest-state display of a number field: grouped in the viewer's locale
 *  when the field opts in, with only the decimals the value carries, capped
 *  at the field's decimals — `343434` or `343,434`, `3.6`, `-1234.57`. */
export function formatNumberValue(
  value: number,
  config: CustomFieldConfig | null | undefined,
): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: numberDecimalPlaces(config),
    useGrouping: numberThousandsSeparator(config),
  }).format(value);
}
